import React, { useState, useEffect } from "react";
import "./cabCard.css";
import "./CabListingCard.css";
import { FaCar } from "react-icons/fa";
import { getValidSession } from "./helpers/sessionUtils";
import LoginPopup from "./popup/LoginPopup";
import ReviewModal from "./popup/ReviewModal";
import axios from "axios";
import { toast } from "react-toastify";
import { buildVendorProfileUrl } from "../utils/vendorProfileUrl";
import ListingVerifiedBadge from "../components/common/ListingVerifiedBadge";
import VendorProfileButton from "../components/common/VendorProfileButton";
import { formatPrice } from "../utils/priceUtils";

// SAFE fallback image
const fallbackImage = "/assets/img/cabs/cab-no-image.png";
const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
const vendorProfileUrl = (item) => buildVendorProfileUrl(item?.Vendor);

export default function CabCard({ cab }) {
  const [open, setOpen] = useState(false);
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [showThanksModal, setShowThanksModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reviewClickPending, setReviewClickPending] = useState(false);

  // Auth State
  const [user, setUser] = useState(() => {
    const session = getValidSession();
    return session?.user?.user || session?.user || null;
  });

  // Sync user state if session changes
  useEffect(() => {
    const session = getValidSession();
    setUser(session?.user?.user || session?.user || null);
  }, [showLoginPopup]);

  // Activity Lead Recording
  useEffect(() => {
    if (open && user && cab && cab.id && !sessionStorage.getItem(`activity_lead_cab_${cab.id}`)) {
      const sendActivityLead = async () => {
        try {
          const currentToken = localStorage.getItem('authToken') || localStorage.getItem('token');
          const headers = currentToken ? { Authorization: `Bearer ${currentToken}` } : {};
          
          await axios.post(`${API_URL}/enquiries/activity`, {
            type: 'cab',
            type_id: cab.id,
            mobile: user.phone || user.mobile || '',
            name: user.name || 'Guest',
            email: user.email || '',
            city: cab.city || ''
          }, { headers });
          
          sessionStorage.setItem(`activity_lead_cab_${cab.id}`, 'true');
        } catch (e) {
          console.error("Activity lead error:", e);
        }
      };
      
      if (user.phone || user.mobile) {
        sendActivityLead();
      }
    }
  }, [open, user, cab]);

  // Prevent crashes if cab is undefined
  if (!cab) return null;

  const imgUrl =
    cab?.Images?.[0]?.image_url ||
    cab?.image_url ||
    fallbackImage;

  const title = cab?.brand_name || cab?.name || "Cab";
  const accommodation_type = cab?.accommodation_type || "AC Cab";
  const seating_capacity = cab?.seating_capacity || "4";
  const kmsIncluded =
    cab?.km_included ||
    cab?.km ||
    cab?.included_km ||
    "1759";

  const perKm =
    cab?.post_limit_rate ||
    cab?.post_limit ||
    "₹17.5/km";

  const price =
    cab?.offer_price ||
    cab?.price ||
    cab?.package_price ||
    "₹8,310";

  const discount =
    cab?.discount ||
    cab?.offer_percent ||
    null;

  const footerInfo = cab?.bottom_info || "Cab with Luggage Carrier @ ₹149";

  const description = cab?.cab_description || "";

  const reviews = cab?.reviews || [];
  const reviewCount = reviews.length;
  const avgRating =
    reviewCount > 0
      ? reviews.reduce((sum, r) => sum + parseFloat(r.rating || 0), 0) / reviewCount
      : cab?.avgRating || cab?.avg_rating || 0;
  const totalReviews = cab?.reviewCount ?? cab?.review_count ?? reviewCount;

  const fullStars = Math.floor(avgRating);
  const hasHalf = avgRating - fullStars >= 0.5;

  const handleAction = (e, type) => {
    e.preventDefault();
    e.stopPropagation();

    if (isSubmitting) return;

    setPendingAction(type);

    if (!user) {
      setShowLoginPopup(true);
      return;
    }

    processLead(type);
  };

  const handleLoginSuccess = (loggedInUser) => {
    const session = getValidSession();
    const resolvedUser = session?.user?.user || session?.user || loggedInUser;
    setUser(resolvedUser);
    setShowLoginPopup(false);

    if (reviewClickPending) {
      setReviewClickPending(false);
      setTimeout(() => {
        setShowReviewModal(true);
      }, 300);
    } else if (pendingAction) {
      processLead(pendingAction, resolvedUser);
    }
  };

  const isPreviewMode = Boolean(localStorage.getItem('superAdminToken')) || new URLSearchParams(window.location.search).get('preview') === 'true';

  const processLead = async (type, userData = null) => {
    if (isPreviewMode) {
      setShowThanksModal(true);
      return;
    }
    setIsSubmitting(true); // Ensure it's set
    const currentUser = userData || user;

    const payload = {
      name: currentUser?.name || "Guest",
      email: currentUser?.email || "",
      mobile: String(currentUser?.phone || currentUser?.mobile || "").replace(/\D/g, "").slice(-10),
      type: "cab",
      type_id: cab.id,
      message: `User clicked ${type} for ${title} (${accommodation_type})`
    };

    let success = false;
    try {
      const res = await axios.post(`${API_URL}/enquiries/create`, payload);
      if (res.data.success) {
        success = true;
        setShowThanksModal(true);

        // Auto-redirect after delay
        setTimeout(() => {
          handleFinalRedirect(type);
        }, 2500);
      }
    } catch (err) {
      toast.error("Error processing request. Please try again.");
      console.error(err);
    } finally {
      setIsSubmitting(false);
      // If we are not showing the success modal, reset the pending action
      // so the button text reverts from "Connecting..."
      if (!success) {
        setPendingAction(null);
      }
    }
  };

  const handleFinalRedirect = (type) => {
    setShowThanksModal(false);
    if (type === "call") {
      window.location.href = `tel:${cab?.cab_contact_number}`;
    } else if (type === "whatsapp") {
      window.open(`https://wa.me/${cab?.cab_contact_number}`, "_blank");
    } else if (type === "book") {
      toast.info("Proceeding to booking...");
      // Add booking logic here if any
    }
    setPendingAction(null);
  };

  const handleReviewClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!user) {
      setReviewClickPending(true);
      setShowLoginPopup(true);
      return;
    }

    setShowReviewModal(true);
  };

  return (
    <div className="cab-card">
      <div className="cab-card-inner">
        {/* Left - Image */}
        <div className="cab-left">
          <div className="cab-image-wrap">
            <img
              src={imgUrl}
              alt={title}
              className="cab-image"
              onError={(e) => (e.target.src = fallbackImage)}
            />
            <ListingVerifiedBadge item={cab} />
          </div>
        </div>

        {/* Middle - Details */}
        <div className="cab-mid">
          <div className="cab-title-row d-flex justify-content-between align-items-start">
            <h3 className="cab-title">{title}</h3>
            <div className="price-title-wrap text-end">
              <div className="d-flex align-items-center justify-content-end gap-2 flex-wrap mb-1">
                {discount && <span className="discount-pill badge bg-success-subtle text-success" style={{ fontSize: '0.7rem' }}>{discount}% OFF</span>}
                {cab?.offer_price && cab?.package_price && Number(cab.package_price) > Number(cab.offer_price) && (
                  <del className="text-muted" style={{ fontSize: '0.85rem', textDecoration: 'line-through' }}>
                    ₹{formatPrice(String(cab.package_price).replace("₹", "").replace(".00", ""))}
                  </del>
                )}
                <span className="price-big-small fw-bold text-primary mb-0">₹{formatPrice(String(price).replace("₹", "").replace(".00", ""))}</span>
              </div>
              <div className="text-muted" style={{ fontSize: '0.75rem', lineHeight: '1.2' }}>
                Inc. GST & DA <br /> Exc. Toll Tax & Parking
              </div>
            </div>
          </div>

          <div className="cab-sub d-flex align-items-center gap-2 flex-wrap">
            <span>or equivalent | {seating_capacity} seater {accommodation_type}</span>
            {vendorProfileUrl(cab) && (
              <VendorProfileButton to={vendorProfileUrl(cab)} />
            )}
          </div>

          <div className="cab-meta-row">
            <div className="cab-meta-item">
              <span className="meta-icon car-meta-icon"><FaCar /></span>
              <span>Driver allowance included</span>
            </div>
            <div className="cab-meta-item">
              <span className="meta-icon emoji-meta-icon">🛣️</span>
              <span>{kmsIncluded} kms included | Post limit: {perKm}</span>
            </div>
          </div>

          <button className="toggle-inclusions" onClick={() => setOpen((s) => !s)}>
            Inclusions and Exclusions {open ? "▴" : "▾"}
          </button>

          <div className="cab-badges">
            <div className="badge">
              Driver allowance included
            </div>

            <div className="badge">
              <strong>{kmsIncluded}</strong> kms included | Post limit: {perKm}
            </div>
          </div>

          {open && (
            <div className="inclusions">
              <h4>Description</h4>
              {description ? (
                <div
                  className="description-content"
                  dangerouslySetInnerHTML={{ __html: description }}
                />
              ) : (
                <p>No description available</p>
              )}
            </div>
          )}
        </div>

        {/* Right - Price & Actions */}
        <div className="cab-right">
          <div className="review-section mb-3 w-100 cab-review-section">
            <div className="d-flex flex-nowrap align-items-center justify-content-center justify-content-md-end gap-2">
              <div className="d-flex flex-nowrap align-items-center gap-2" style={{ cursor: 'pointer' }} onClick={handleReviewClick}>
                <div className="d-flex" style={{ gap: "2px" }}>
                  {[1, 2, 3, 4, 5].map((star) => {
                    if (star <= fullStars) {
                      return (
                        <i key={star} className="bi bi-star-fill text-warning" style={{ fontSize: "16px" }} />
                      );
                    } else if (star === fullStars + 1 && hasHalf) {
                      return (
                        <i key={star} className="bi bi-star-half text-warning" style={{ fontSize: "16px" }} />
                      );
                    } else {
                      return (
                        <i key={star} className="bi bi-star text-secondary opacity-50" style={{ fontSize: "16px" }} />
                      );
                    }
                  })}
                </div>
                <div className="d-flex align-items-baseline gap-1" style={{ whiteSpace: "nowrap" }}>
                  <span className="fw-bold text-dark" style={{ fontSize: "18px" }}>
                    {Number(avgRating).toFixed(1)}/5
                  </span>
                  <span className="text-muted" style={{ fontSize: "13px" }}>
                    ({totalReviews} reviews)
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="w-100"> {/* Mobile: 3 icon-only buttons in a row (below 900px, matching cabCard.css breakpoint) */}
            <div className="cab-mobile-action-row">
              <div className="cab-mobile-rating">
                <div className="d-flex flex-nowrap align-items-center gap-2">
                  <div className="d-flex flex-nowrap align-items-center gap-2" style={{ cursor: 'pointer' }} onClick={handleReviewClick}>
                    <div className="d-flex" style={{ gap: "2px" }}>
                      {[1, 2, 3, 4, 5].map((star) => {
                        if (star <= fullStars) {
                          return (
                            <i key={star} className="bi bi-star-fill text-warning" style={{ fontSize: "16px" }} />
                          );
                        } else if (star === fullStars + 1 && hasHalf) {
                          return (
                            <i key={star} className="bi bi-star-half text-warning" style={{ fontSize: "16px" }} />
                          );
                        }
                        return (
                          <i key={star} className="bi bi-star text-secondary opacity-50" style={{ fontSize: "16px" }} />
                        );
                      })}
                    </div>
                    <div className="d-flex align-items-baseline gap-1" style={{ whiteSpace: "nowrap" }}>
                      <span className="fw-bold text-dark" style={{ fontSize: "18px" }}>
                        {Number(avgRating).toFixed(1)}/5
                      </span>
                      <span className="text-muted" style={{ fontSize: "13px" }}>
                        ({totalReviews} reviews)
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="cab-mobile-icons">
                <button
                  className="cab-mobile-icon-btn"
                  onClick={(e) => handleAction(e, "call")}
                  disabled={isSubmitting}
                  title="Call Now"
                >
                  {isSubmitting && pendingAction === "call" ? (
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  ) : (
                    <img src="/assets/img/mobile-icon/call.png" alt="Call" />
                  )}
                </button>
                <button
                  className="cab-mobile-icon-btn"
                  onClick={(e) => handleAction(e, "whatsapp")}
                  disabled={isSubmitting}
                  title="Contact Owner"
                >
                  {isSubmitting && pendingAction === "whatsapp" ? (
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  ) : (
                    <img src="/assets/img/mobile-icon/whatsapp.png" alt="WhatsApp" />
                  )}
                </button>
              </div>
            </div>

            <div className="cab-btn-mobile gap-2 justify-content-center">
              <button
                className="btn btn-outline-primary fw-bold rounded-3 d-flex align-items-center justify-content-center"
                style={{ width: '52px', height: '44px', flexShrink: 0, cursor: 'pointer' }}
                onClick={(e) => handleAction(e, "call")}
                disabled={isSubmitting}
                title="Call Now"
              >
                {isSubmitting && pendingAction === "call" ? (
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                ) : (
                  <i className="bi bi-telephone-fill"></i>
                )}
              </button>
              <button
                className="btn btn-outline-success fw-bold rounded-3 d-flex align-items-center justify-content-center"
                style={{ width: '52px', height: '44px', flexShrink: 0, cursor: 'pointer' }}
                onClick={(e) => handleAction(e, "whatsapp")}
                disabled={isSubmitting}
                title="Contact Owner"
              >
                {isSubmitting && pendingAction === "whatsapp" ? (
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                ) : (
                  <i className="bi bi-whatsapp"></i>
                )}
              </button>
              <button
                className="btn btn-outline-dark fw-bold rounded-3 d-flex align-items-center justify-content-center listing-book-btn"
                style={{ width: '52px', height: '44px', flexShrink: 0, cursor: 'pointer' }}
                onClick={(e) => handleAction(e, "book")}
                disabled={isSubmitting}
                title="Book Now"
              >
                {isSubmitting && pendingAction === "book" ? (
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                ) : (
                  <i className="bi bi-calendar-check-fill"></i>
                )}
              </button>
            </div>
 
            {/* Desktop: stacked full-width buttons (above 900px) */}
            <div className="cab-btn-desktop flex-column gap-2">
              <button
                className="btn btn-outline-primary fw-bold py-2 rounded-3 text-truncate d-flex align-items-center justify-content-center"
                onClick={(e) => handleAction(e, "call")}
                disabled={isSubmitting}
              >
                {isSubmitting && pendingAction === "call" ? (
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                ) : (
                  <i className="bi bi-telephone-fill me-2"></i>
                )}
                {isSubmitting && pendingAction === "call" ? "Connecting..." : "Call Now"}
              </button>
              <button
                className="btn btn-outline-success fw-bold py-2 rounded-3 text-truncate d-flex align-items-center justify-content-center"
                onClick={(e) => handleAction(e, "whatsapp")}
                disabled={isSubmitting}
              >
                {isSubmitting && pendingAction === "whatsapp" ? (
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                ) : (
                  <i className="bi bi-whatsapp me-2"></i>
                )}
                {isSubmitting && pendingAction === "whatsapp" ? "Connecting..." : "Contact Owner"}
              </button>
              <button
                className="select-btn listing-book-btn d-flex align-items-center justify-content-center"
                onClick={(e) => handleAction(e, "book")}
                disabled={isSubmitting}
                style={{ minHeight: '44px', cursor: 'pointer' }}
              >
                {isSubmitting && pendingAction === "book" ? (
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                ) : null}
                {isSubmitting && pendingAction === "book" ? "Processing..." : "Book Now"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="cab-footer-bar">
        <span>{footerInfo}</span>
      </div>

      {/* --- LOGIN POPUP MODAL --- */}
      {showLoginPopup && (
        <div className="custom-modal-overlay" onClick={() => {
          setShowLoginPopup(false);
          setIsSubmitting(false);
          setPendingAction(null);
        }}>
          <div onClick={(e) => e.stopPropagation()}>
            <LoginPopup
              visible={showLoginPopup}
              onClose={() => {
                setShowLoginPopup(false);
                setIsSubmitting(false);
                setPendingAction(null);
              }}
              onLoginSuccess={handleLoginSuccess}
              serviceType="Cab"
            />
          </div>
        </div>
      )}

      {/* --- THANK YOU MODAL --- */}
      {(isSubmitting || showThanksModal) && !showLoginPopup && (
        <div className="custom-modal-overlay" style={{ zIndex: 9999999 }}>
          <div className="thanks-modal card shadow-lg border-0 rounded-4 text-center p-5" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            {isSubmitting && !showThanksModal ? (
              <>
                <div className="mb-4">
                  <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem', borderWidth: '0.3em' }}>
                    <span className="visually-hidden">Processing...</span>
                  </div>
                </div>
                <h4 className="fw-bold mb-2">Processing your request...</h4>
                <p className="text-muted mb-0">Please wait while we save your lead and notify the vendor.</p>
              </>
            ) : (
              <>
                <div className="mb-4">
                  <div className="success-checkmark-wrap">
                    <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '4rem' }}></i>
                  </div>
                </div>
                <h2 className="fw-bold mb-3">Thank You!</h2>
                <p className="text-muted mb-4">Your request has been sent to the team. We will get back to you shortly.</p>
                <button className="btn btn-primary rounded-pill px-4 py-2 fw-bold" onClick={() => handleFinalRedirect(pendingAction)}>
                  OK
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* --- REVIEW MODAL --- */}
      <ReviewModal
        isOpen={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        entity={cab}
        entityType="cab"
        user={user}
        apiEndpoint="/cabs"
      />
    </div>
  );
}
